import { useCallback, useMemo, useState } from "react";
import { ensureExhaustive, useDeviceType } from "@b2bportal/core-utilities";
import type { AutocompleteCategory } from "@b2bportal/ground-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import type { CarLocationAutocompleteProps } from "./CarLocationAutocomplete";
import type { CarLocationSuggestion } from "./types";

export const useCarLocationAutocomplete = (
  props: CarLocationAutocompleteProps
) => {
  const { t } = useI18nContext();
  const {
    id,
    value,
    label,
    onChange,
    onOpen,
    onFocus,
    className,
    placeholder,
  } = props;
  const [open, setOpen] = useState(false);

  const openDialog = useCallback(() => {
    setOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setOpen(false);
  }, []);

  const { isDesktopAndUp } = useDeviceType();

  const groupBy = (val: CarLocationSuggestion) => val.category;
  const getGroupLabelKey = useCallback(
    (val: AutocompleteCategory) => {
      switch (val) {
        case "airports":
          return t("core-cars.search.category.airports");
        case "cities":
          return t("core-cars.search.category.cities");
        case "emptyMatch":
          return t("core-cars.search.category.emptyMatch");
        case "featuredGetaways":
          return t("core-cars.search.category.featuredGetaways");
        case "hotelMarkets":
          return t("core-cars.search.category.hotelMarkets");
        case "nearby":
          return t("core-cars.search.category.nearby");
        case "places":
          return t("core-cars.search.category.places");
        case "recentSearches":
          return t("core-cars.search.category.recentSearches");
        default:
          return ensureExhaustive(val, "");
      }
    },
    [t]
  );

  return useMemo(() => {
    return {
      context: {
        id,
        value,
        label,
        className,
        placeholder,
        open,
        showLocationInput: isDesktopAndUp,
        error: props.error,
        useDialogOnMobile: props.useDialogOnMobile ?? true,
      },
      handlers: {
        onChange: (val: CarLocationSuggestion | null) => {
          if (val != null) {
            onChange(val);
            closeDialog();
          }
        },
        onOpen,
        onFocus,
        openDialog,
        closeDialog,
        groupBy,
        getGroupLabelKey,
      },
    };
  }, [
    className,
    closeDialog,
    getGroupLabelKey,
    id,
    isDesktopAndUp,
    label,
    onChange,
    onOpen,
    onFocus,
    open,
    openDialog,
    placeholder,
    props.error,
    props.useDialogOnMobile,
    value,
  ]);
};
